import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import SEOHead from "@/components/feature/SEOHead";

type Status = "idle" | "submitting" | "success" | "error";

function generateChallenge() {
  const a = Math.floor(Math.random() * 9) + 1;
  const b = Math.floor(Math.random() * 9) + 1;
  return { a, b, answer: a + b };
}

export default function WantMorePage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>("idle");
  const [charCount, setCharCount] = useState(0);
  const [challenge, setChallenge] = useState(generateChallenge);
  const [captchaValue, setCaptchaValue] = useState("");
  const [captchaError, setCaptchaError] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const refreshChallenge = useCallback(() => {
    setChallenge(generateChallenge());
    setCaptchaValue("");
    setCaptchaError(false);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    // Honeypot check — bots fill this, humans don't
    const honeypot = (form.elements.namedItem("website") as HTMLInputElement)?.value;
    if (honeypot) return;

    // Math CAPTCHA check
    const guess = parseInt(captchaValue, 10);
    if (isNaN(guess) || guess !== challenge.answer) {
      setCaptchaError(true);
      refreshChallenge();
      return;
    }

    const message = (form.elements.namedItem("message") as HTMLTextAreaElement)?.value ?? "";
    if (message.length > 500) return;

    setStatus("submitting");

    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      if (key !== "website") data.append(key, value as string);
    });

    try {
      const res = await fetch("https://readdy.ai/api/form/d75u55jq7u9ee9hce5q0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        setCharCount(0);
        setCaptchaValue("");
        refreshChallenge();
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="bg-[#0A0A0A] min-h-screen w-full">
      <SEOHead
        title="I Want More | The Wraith Chronicles"
        description="Tell the author what you felt. Leave a message for the writer of The Wraith Chronicles."
        noIndex
      />
      <Navbar />

      {/* Page Hero */}
      <div className="relative w-full pt-36 pb-20 px-6 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, transparent, transparent 28px, #C8922A 28px, #C8922A 29px)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-transparent to-[#0A0A0A]" />

        <div className="relative max-w-2xl mx-auto text-center">
          <div className="flex items-center justify-center gap-4 mb-10">
            <div className="w-12 h-px bg-[#C8922A]/30" />
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-fire-line text-[#C8922A]/50 text-base" />
            </div>
            <div className="w-12 h-px bg-[#C8922A]/30" />
          </div>

          <h1 className="font-['Special_Elite'] text-white text-5xl md:text-6xl tracking-[0.06em] uppercase mb-6 leading-tight">
            I Want<br />
            <span className="text-[#C8922A]">More.</span>
          </h1>

          <p className="font-['Share_Tech_Mono'] text-[#6A6A6A] text-sm tracking-[0.2em] uppercase mb-4">
            You reached the end. That means something.
          </p>
          <p className="font-['Special_Elite'] text-[#555] text-lg italic leading-relaxed">
            Tell the author what you felt. What you want next.<br />
            What you&apos;re not ready to let go of yet.
          </p>
        </div>
      </div>

      {/* Divider */}
      <div className="max-w-2xl mx-auto px-6">
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px bg-[#1E1E1E]" />
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-pencil-line text-[#C8922A]/30 text-xs" />
          </div>
          <div className="flex-1 h-px bg-[#1E1E1E]" />
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl mx-auto px-6 py-16">
        {status === "success" ? (
          <div className="text-center py-16">
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="w-12 h-px bg-[#C8922A]/30" />
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-check-line text-[#C8922A] text-lg" />
              </div>
              <div className="w-12 h-px bg-[#C8922A]/30" />
            </div>
            <h2 className="font-['Special_Elite'] text-white text-3xl mb-4 tracking-wide">
              Signal received.
            </h2>
            <p className="font-['Share_Tech_Mono'] text-[#6A6A6A] text-sm tracking-[0.2em] uppercase mb-10">
              The author will know you&apos;re out there.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => setStatus("idle")}
                className="flex items-center gap-2 border border-[#C8922A]/40 hover:border-[#C8922A] text-[#C8922A] text-xs tracking-[0.15em] uppercase font-['Share_Tech_Mono'] px-5 py-3 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line text-sm" />
                Send Another
              </button>
              <button
                onClick={() => navigate("/book")}
                className="flex items-center gap-2 bg-[#C8922A]/10 border border-[#C8922A]/30 hover:bg-[#C8922A]/20 text-[#C8922A]/70 text-xs tracking-[0.15em] uppercase font-['Share_Tech_Mono'] px-5 py-3 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line text-sm" />
                Back to The Book
              </button>
            </div>
          </div>
        ) : (
          <form
            ref={formRef}
            id="want-more-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="flex flex-col gap-6"
          >
            {/* Honeypot — hidden from humans, bots fill this */}
            <div style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none" }} aria-hidden="true">
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            {/* Name + Email row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="name"
                  className="font-['Share_Tech_Mono'] text-[#555] text-xs tracking-[0.25em] uppercase"
                >
                  Your Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Jordan, Aidan, Nina..."
                  required
                  className="bg-[#0E0E0E] border border-[#1E1E1E] focus:border-[#C8922A]/50 text-white text-sm font-['Share_Tech_Mono'] placeholder-[#333] px-4 py-3 outline-none transition-all duration-300"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="email"
                  className="font-['Share_Tech_Mono'] text-[#555] text-xs tracking-[0.25em] uppercase"
                >
                  Email <span className="text-[#333]">(optional)</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="for updates if there&apos;s more"
                  className="bg-[#0E0E0E] border border-[#1E1E1E] focus:border-[#C8922A]/50 text-white text-sm font-['Share_Tech_Mono'] placeholder-[#333] px-4 py-3 outline-none transition-all duration-300"
                />
              </div>
            </div>

            {/* What do you want more of */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="want"
                className="font-['Share_Tech_Mono'] text-[#555] text-xs tracking-[0.25em] uppercase"
              >
                What do you want more of?
              </label>
              <select
                id="want"
                name="want"
                required
                defaultValue=""
                className="bg-[#0E0E0E] border border-[#1E1E1E] focus:border-[#C8922A]/50 text-white text-sm font-['Share_Tech_Mono'] px-4 py-3 outline-none transition-all duration-300 cursor-pointer appearance-none"
              >
                <option value="" disabled>Select what hit hardest...</option>
                <option value="Jordan and Aidan">Jordan and Aidan — I&apos;m not done with them</option>
                <option value="Nina">Nina — her story deserves more</option>
                <option value="The world">The world — I want to know more about how it fell</option>
                <option value="The action">The action and survival</option>
                <option value="Everything">All of it. Just more of all of it.</option>
              </select>
            </div>

            {/* Message */}
            <div className="flex flex-col gap-2">
              <div className="flex items-end justify-between">
                <label
                  htmlFor="message"
                  className="font-['Share_Tech_Mono'] text-[#555] text-xs tracking-[0.25em] uppercase"
                >
                  Say something
                </label>
                <span
                  className={`font-['Share_Tech_Mono'] text-xs transition-colors ${
                    charCount > 450 ? "text-[#8B1A1A]" : "text-[#333]"
                  }`}
                >
                  {charCount}/500
                </span>
              </div>
              <textarea
                id="message"
                name="message"
                rows={6}
                required
                maxLength={500}
                placeholder="What stayed with you? What do you want to know? What would you fight for?"
                onChange={(e) => setCharCount(e.target.value.length)}
                className="bg-[#0E0E0E] border border-[#1E1E1E] focus:border-[#C8922A]/50 text-white text-sm font-['Share_Tech_Mono'] placeholder-[#333] px-4 py-3 outline-none transition-all duration-300 resize-none leading-relaxed"
              />
            </div>

            {/* Human Verification */}
            <div className="border border-[#1A1A1A] bg-[#0D0D0D] px-5 py-5 flex flex-col gap-3">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-shield-check-line text-[#C8922A]/50 text-xs" />
                </div>
                <span className="font-['Share_Tech_Mono'] text-[#444] text-xs tracking-[0.25em] uppercase">
                  Prove you&apos;re human
                </span>
              </div>

              <div className="flex items-center gap-4">
                {/* Math challenge display */}
                <div className="flex items-center gap-3 flex-1">
                  <div className="font-['Special_Elite'] text-[#C8922A] text-2xl tracking-widest select-none">
                    {challenge.a}
                  </div>
                  <div className="font-['Share_Tech_Mono'] text-[#444] text-lg">+</div>
                  <div className="font-['Special_Elite'] text-[#C8922A] text-2xl tracking-widest select-none">
                    {challenge.b}
                  </div>
                  <div className="font-['Share_Tech_Mono'] text-[#444] text-lg">=</div>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={captchaValue}
                    onChange={(e) => {
                      setCaptchaValue(e.target.value);
                      setCaptchaError(false);
                    }}
                    placeholder="?"
                    className={`w-16 bg-[#0A0A0A] border text-white text-sm font-['Share_Tech_Mono'] text-center px-2 py-2 outline-none transition-all duration-300 ${
                      captchaError
                        ? "border-[#8B1A1A] focus:border-[#8B1A1A]"
                        : "border-[#2A2A2A] focus:border-[#C8922A]/50"
                    }`}
                  />
                </div>

                {/* Refresh challenge */}
                <button
                  type="button"
                  onClick={refreshChallenge}
                  className="w-8 h-8 flex items-center justify-center text-[#333] hover:text-[#C8922A]/60 transition-colors cursor-pointer"
                  title="New question"
                >
                  <i className="ri-refresh-line text-sm" />
                </button>
              </div>

              {captchaError && (
                <p className="font-['Share_Tech_Mono'] text-[#8B1A1A] text-xs tracking-[0.15em] uppercase">
                  Wrong answer — new question loaded. Try again.
                </p>
              )}
            </div>

            {status === "error" && (
              <div className="border border-[#8B1A1A]/40 bg-[#8B1A1A]/10 px-4 py-3">
                <p className="font-['Share_Tech_Mono'] text-[#8B1A1A] text-xs tracking-[0.15em] uppercase">
                  Signal failed. Check your connection and try again.
                </p>
              </div>
            )}

            {/* Submit */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
              <p className="font-['Special_Elite'] text-[#333] text-sm italic text-center sm:text-left">
                &ldquo;If you still laugh, you&apos;re still winning.&rdquo;
              </p>
              <button
                type="submit"
                disabled={status === "submitting" || charCount > 500 || captchaValue === ""}
                className="flex items-center gap-2 bg-[#C8922A] hover:bg-[#D9A030] disabled:opacity-40 disabled:cursor-not-allowed text-black text-xs tracking-[0.2em] uppercase font-['Share_Tech_Mono'] px-8 py-3 transition-all duration-300 cursor-pointer whitespace-nowrap font-bold"
              >
                {status === "submitting" ? (
                  <>
                    <i className="ri-loader-4-line text-sm animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line text-sm" />
                    Send Signal
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Bottom back link */}
      <div className="max-w-2xl mx-auto px-6 pb-20 text-center">
        <button
          onClick={() => navigate("/book")}
          className="font-['Share_Tech_Mono'] text-[#333] hover:text-[#C8922A]/60 text-xs tracking-[0.25em] uppercase transition-colors duration-300 cursor-pointer flex items-center gap-2 mx-auto"
        >
          <i className="ri-arrow-left-line text-xs" />
          Back to The Book
        </button>
      </div>

      <Footer />
    </div>
  );
}
